import { showDialog } from '@/utils/dialogs';
import React, { useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  Dimensions,
  Modal,
  Platform,
  Pressable,
  Text,
  TextInput,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from 'react-native';
import { KeyboardAvoidingView } from 'react-native-keyboard-controller';
import { usePathname } from 'expo-router';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { MaterialIcons } from '@expo/vector-icons';
import Constants from 'expo-constants';

import { apiRequest } from '@/services/apiClient';
import { useAuth } from '@/context/AuthContext';
import { useToast } from '@/context/ToastContext';
import { useTranslation } from '@/hooks/useTranslation';
import { useResponsive } from '@/hooks/useResponsive';
import { registerFeedbackOpener, usesHeaderFeedbackEntry } from './feedbackLauncher';

type FeedbackCategory = 'bug' | 'suggestion' | 'praise' | 'question' | 'other';

const CATEGORY_OPTIONS: { key: FeedbackCategory; icon: string }[] = [
  { key: 'bug', icon: 'bug-report' },
  { key: 'suggestion', icon: 'lightbulb-outline' },
  { key: 'praise', icon: 'sentiment-satisfied-alt' },
  { key: 'question', icon: 'help-outline' },
  { key: 'other', icon: 'chat-bubble-outline' },
];

function getAppVersion(): string | undefined {
  const expoVersion = Constants.expoConfig?.version;
  const expoConfig: any = Constants.expoConfig ?? {};
  const manifestExtra: any = (Constants as any).manifest2?.extra ?? {};
  const runtimeVersion = expoConfig.runtimeVersion || manifestExtra.runtimeVersion;
  const jsEngine = Platform.OS === 'web' ? 'web' : Constants.jsEngine;

  const versionParts = [expoVersion, runtimeVersion, jsEngine].filter(Boolean).join(' | ');
  return versionParts || undefined;
}

export function FeedbackFab() {
  const { phase, billingStatus } = useAuth();
  const { t } = useTranslation();
  const { showToast } = useToast();
  const pathname = usePathname();
  const insets = useSafeAreaInsets();
  const { width } = useWindowDimensions();
  const { isDesktop } = useResponsive();
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState<FeedbackCategory>('bug');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const categories = useMemo(
    () =>
      CATEGORY_OPTIONS.map((item) => ({
        ...item,
        label: t(`feedback.categories.${item.key}`),
      })),
    [t]
  );

  const shouldRender = phase === 'authenticated';
  // Phone-width web (<760): a floating button would cover the WebAppShell
  // bottom bar and the chat composer, so the shell's phone header shows a
  // feedback icon that opens this sheet through feedbackLauncher (ADM-12).
  const showFloatingButton = !usesHeaderFeedbackEntry(Platform.OS, width);
  const appVersion = useMemo(() => getAppVersion(), []);
  const compactWebFab = Platform.OS === 'web' && !isDesktop;

  useEffect(() => {
    if (!shouldRender) return undefined;
    return registerFeedbackOpener(() => setOpen(true));
  }, [shouldRender]);

  if (!shouldRender) {
    return null;
  }

  const resetForm = () => {
    setCategory('bug');
    setMessage('');
  };

  const handleSubmit = async () => {
    const trimmed = message.trim();
    if (trimmed.length < 6) {
      // Toast (not a dialog): native alerts are a no-op on React Native Web, so on the
      // web app the button appeared to do nothing. useToast works everywhere.
      showToast(t('feedback.errorTooShort'), 'error');
      return;
    }

    try {
      setSubmitting(true);
      await apiRequest({
        endpoint: '/feedback',
        method: 'POST',
        data: {
          category,
          message: trimmed,
          route: pathname,
          platform: Platform.OS,
          app_version: appVersion,
          metadata: {
            phase,
            billing_status: billingStatus,
            window: Platform.OS === 'web' ? { href: globalThis.location?.href } : undefined,
            screen: Dimensions.get('window'),
          },
        },
      });
      setOpen(false);
      showToast(`${t('feedback.successTitle')} ${t('feedback.successMessage')}`, 'success');
      resetForm();
    } catch (error) {
      console.error('Feedback submission failed', error);
      showToast(`${t('feedback.errorTitle')} — ${t('feedback.errorMessage')}`, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {showFloatingButton && (
        <TouchableOpacity
          activeOpacity={0.85}
          onPress={() => setOpen(true)}
          testID="feedback-fab"
          accessibilityRole="button"
          accessibilityLabel={t('feedback.button')}
          style={[
            {
              position: 'absolute',
              right: 20,
              bottom: insets.bottom + 24,
              backgroundColor: '#1D4ED8',
              borderRadius: 28,
              paddingHorizontal: 18,
              paddingVertical: 12,
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              shadowColor: '#000',
              shadowOpacity: 0.25,
              shadowRadius: 8,
              shadowOffset: { width: 0, height: 4 },
              elevation: 6,
              zIndex: 999,
            },
            compactWebFab && {
              width: 46,
              height: 46,
              right: 16,
              bottom: insets.bottom + 16,
              paddingHorizontal: 0,
              paddingVertical: 0,
            },
          ]}
        >
          <MaterialIcons name="feedback" size={20} color="#fff" />
          {!compactWebFab && (
            <Text style={{ color: '#fff', fontWeight: '600', marginLeft: 8 }}>{t('feedback.button')}</Text>
          )}
        </TouchableOpacity>
      )}

      <Modal animationType="slide" visible={open} transparent>
        <Pressable
          onPress={() => (submitting ? null : setOpen(false))}
          style={{ flex: 1, backgroundColor: 'rgba(15, 23, 42, 0.45)' }}
        />
        <KeyboardAvoidingView
          behavior={Platform.OS === 'ios' ? 'padding' : undefined}
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: '#f8fafc',
            borderTopLeftRadius: 24,
            borderTopRightRadius: 24,
            paddingBottom: insets.bottom + 16,
            paddingHorizontal: 20,
            paddingTop: 20,
          }}
        >
          <View style={{ alignItems: 'center', marginBottom: 12 }}>
            <View style={{ width: 40, height: 4, borderRadius: 9999, backgroundColor: '#d1d5db' }} />
          </View>
          <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 16 }}>
            <MaterialIcons name="feedback" size={24} color="#1D4ED8" />
            <Text style={{ marginLeft: 8, fontSize: 18, fontWeight: '700', color: '#0f172a' }}>
              {t('feedback.modalTitle')}
            </Text>
          </View>
          <Text style={{ fontSize: 13, color: '#475569', marginBottom: 16 }}>
            {t('feedback.modalDescription')}
          </Text>

          <View style={{ flexDirection: 'row', flexWrap: 'wrap', marginBottom: 16 }}>
            {categories.map((item) => {
              const isActive = item.key === category;
              return (
                <TouchableOpacity
                  key={item.key}
                  activeOpacity={0.85}
                  onPress={() => setCategory(item.key)}
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    marginRight: 8,
                    marginBottom: 8,
                    paddingHorizontal: 12,
                    paddingVertical: 8,
                    borderRadius: 16,
                    backgroundColor: isActive ? 'rgba(29, 78, 216, 0.12)' : '#e2e8f0',
                  }}
                >
                  <MaterialIcons
                    name={item.icon as keyof typeof MaterialIcons.glyphMap}
                    size={16}
                    color={isActive ? '#1D4ED8' : '#475569'}
                  />
                  <Text
                    style={{
                      marginLeft: 6,
                      fontSize: 13,
                      fontWeight: '600',
                      color: isActive ? '#1D4ED8' : '#334155',
                    }}
                  >
                    {item.label}
                  </Text>
                </TouchableOpacity>
              );
            })}
          </View>

          <TextInput
            value={message}
            onChangeText={setMessage}
            multiline
            placeholder={t('feedback.messagePlaceholder')}
            placeholderTextColor="#94a3b8"
            style={{
              minHeight: 140,
              borderRadius: 12,
              borderWidth: 1,
              borderColor: '#cbd5f5',
              padding: 14,
              fontSize: 15,
              color: '#111827',
              textAlignVertical: 'top',
              backgroundColor: '#fff',
            }}
            editable={!submitting}
            maxLength={2000}
          />
          <Text style={{ fontSize: 12, color: '#64748b', marginTop: 8, marginBottom: 16 }}>
            {t('feedback.autoContextNote')}
          </Text>

          <View style={{ flexDirection: 'row' }}>
            <View style={{ flex: 1, marginRight: 12 }}>
              <Pressable
                onPress={() => {
                  if (!submitting) {
                    setOpen(false);
                    resetForm();
                  }
                }}
                style={({ pressed }) => ({
                  height: 48,
                  borderRadius: 12,
                  borderWidth: 1,
                  borderColor: '#cbd5f5',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: pressed ? 0.7 : 1,
                  backgroundColor: pressed ? '#f1f5f9' : 'transparent',
                })}
                disabled={submitting}
              >
                <Text style={{ fontWeight: '600', color: '#475569' }}>{t('feedback.cancel')}</Text>
              </Pressable>
            </View>
            <View style={{ flex: 1 }}>
              <Pressable
                onPress={handleSubmit}
                style={({ pressed }) => ({
                  height: 48,
                  borderRadius: 12,
                  backgroundColor: '#1D4ED8',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexDirection: 'row',
                  opacity: submitting ? 0.75 : pressed ? 0.85 : 1,
                  paddingHorizontal: 12,
                })}
                disabled={submitting}
              >
                {submitting ? (
                  <ActivityIndicator color="#fff" size="small" />
                ) : (
                  <MaterialIcons name="send" size={20} color="#fff" style={{ marginRight: 6 }} />
                )}
                <Text style={{ fontWeight: '700', color: '#fff', fontSize: 15 }}>
                  {submitting ? t('feedback.submitting') : t('feedback.submit')}
                </Text>
              </Pressable>
            </View>
          </View>
        </KeyboardAvoidingView>
      </Modal>
    </>
  );
}
